import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  X, Upload, Image, Video, FileText, Eye, Save, 
  Star, Calendar, User, Tag, Link2, Trash2 
} from 'lucide-react';

const EnhancedBlogEditor = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingBlog = null,
  initialData = {}
}) => {
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    content: initialData.content || '',
    author: initialData.author || 'Admin',
    excerpt: initialData.excerpt || '',
    category: initialData.category || 'retail-insights',
    tags: initialData.tags || [],
    published: initialData.published || false,
    is_featured: initialData.is_featured || false,
    featured_image_url: initialData.featured_image_url || '',
    video_url: initialData.video_url || ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const categories = [
    { value: 'retail-insights', label: 'Retail Insights' },
    { value: 'case-studies', label: 'Case Studies' },
    { value: 'technology', label: 'Technology' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'customer-engagement', label: 'Customer Engagement' },
    { value: 'ai-chatbots', label: 'AI & Chatbots' },
    { value: 'qr-discounts', label: 'QR Discounts' },
    { value: 'news', label: 'News & Updates' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // For demo purposes, we'll use a placeholder image URL
      // In production, this would upload to your file storage service
      const mockImageUrl = `https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop`;
      
      handleInputChange('featured_image_url', mockImageUrl);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    const blogData = {
      ...formData,
      updated_at: new Date().toISOString()
    };
    
    onSave(blogData);
  };

  const insertContentAtCursor = (content) => {
    const textarea = document.querySelector('textarea[name="content"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentContent = formData.content;
      const newContent = currentContent.substring(0, start) + content + currentContent.substring(end);
      handleInputChange('content', newContent);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-bold text-charcoal">
                {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h2>
              <p className="text-sm text-gray-600">
                Create engaging content for your audience
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setPreview(!preview)}
              className="border-primary text-primary hover:bg-primary hover:text-white"
            >
              <Eye className="h-4 w-4 mr-2" />
              {preview ? 'Edit' : 'Preview'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {!preview ? (
            /* Edit Mode */
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter compelling blog title..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Author</Label>
                  <Input
                    value={formData.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    placeholder="Author name"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <Label className="text-sm font-medium">Excerpt</Label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  placeholder="Brief description that will appear in previews..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Category and Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => handleInputChange('category', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Tags</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tags..."
                      className="flex-1"
                    />
                    <Button onClick={addTag} size="sm" variant="outline">
                      <Tag className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button onClick={() => removeTag(tag)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Media Upload */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Featured Image</Label>
                  <div className="mt-1 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={formData.featured_image_url}
                        onChange={(e) => handleInputChange('featured_image_url', e.target.value)}
                        placeholder="Image URL or upload file..."
                        className="flex-1"
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    {formData.featured_image_url && (
                      <img 
                        src={formData.featured_image_url} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded border"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Video URL (Optional)</Label>
                  <Input
                    value={formData.video_url}
                    onChange={(e) => handleInputChange('video_url', e.target.value)}
                    placeholder="YouTube or Vimeo URL..."
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Content Editor */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Content *</Label>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => insertContentAtCursor('![Image](image-url-here)')}
                    >
                      <Image className="h-3 w-3 mr-1" />
                      Image
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => insertContentAtCursor('[Link Text](url-here)')}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Link
                    </Button>
                  </div>
                </div>
                <Textarea
                  name="content"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Write your blog content here... (Markdown supported)"
                  rows={16}
                  className="mt-1 font-mono text-sm"
                />
              </div>

              {/* Settings */}
              <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => handleInputChange('published', e.target.checked)}
                  />
                  <span className="text-sm font-medium">Published</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                  />
                  <Star className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Featured Post</span>
                </label>
              </div>
            </div>
          ) : (
            /* Preview Mode */
            <div className="prose prose-lg max-w-none">
              <div className="border-b pb-6 mb-6">
                {formData.featured_image_url && (
                  <img 
                    src={formData.featured_image_url} 
                    alt={formData.title}
                    className="w-full h-64 object-cover rounded-lg mb-4"
                  />
                )}
                <div className="flex items-center gap-2 mb-2">
                  <Badge>{formData.category}</Badge>
                  {formData.is_featured && (
                    <Badge className="bg-amber-100 text-amber-800">
                      <Star className="h-3 w-3 mr-1" />
                      Featured
                    </Badge>
                  )}
                </div>
                <h1 className="text-3xl font-bold mb-3">{formData.title || 'Blog Title'}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {formData.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                {formData.excerpt && (
                  <p className="text-lg text-gray-600 italic">{formData.excerpt}</p>
                )}
              </div>
              <div className="whitespace-pre-wrap">
                {formData.content || 'Blog content will appear here...'}
              </div>
              {formData.video_url && (
                <div className="mt-6">
                  <iframe
                    src={formData.video_url}
                    className="w-full h-64 rounded-lg"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t mt-6">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!formData.title || !formData.content}
              className="bg-primary hover:bg-primary/90"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingBlog ? 'Update' : 'Create'} Blog Post
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedBlogEditor;